import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Collapse, 
  IconButton, 
  Stack, 
  Chip 
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PaymentsIcon from '@mui/icons-material/Payments';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * Component for displaying payment history information
 * 
 * @param {Object} props - Component props
 * @param {Array} props.items - Debit note items from datagrid
 * @param {Array} props.payments - Array of payments
 * @param {number} props.grandTotal - Debit note grand total
 * @param {number} props.totalPaid - Total amount paid
 * @param {number} props.remainingBalance - Remaining balance to be paid
 * @param {number} props.totalBillAmount - Total amount from original invoice
 * @param {string} props.invoiceId - Unique ID of the selected invoice
 */
const DebitNotePaymentSection = ({
  items = [],
  payments = [],
  grandTotal = 0,
  totalPaid = 0,
  remainingBalance = 0,
  totalBillAmount = 0,
  invoiceId = null
}) => {
  const [initialTotalBillAmount, setInitialTotalBillAmount] = useState(0);
  const [currentInvoiceId, setCurrentInvoiceId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    console.log('useEffect triggered with:', {
      invoiceId,
      currentInvoiceId,
      totalBillAmount
    });

    if (invoiceId && invoiceId !== currentInvoiceId) {
      console.log('Setting initial total bill amount to:', totalBillAmount);
      setInitialTotalBillAmount(totalBillAmount);
      setCurrentInvoiceId(invoiceId);
    }
  }, [invoiceId, totalBillAmount, currentInvoiceId]);

  const totalRefundAmount = items.reduce((total, item) => {
    const itemTotal = (item.quantity || 0) * (item.price || 0);
    const discountAmount = (itemTotal * (item.discount || 0)) / 100;
    const afterDiscount = itemTotal - discountAmount;
    const vatAmount = (afterDiscount * (item.vatRate || 0)) / 100;
    return total + (afterDiscount + vatAmount);
  }, 0);

  // Get icon for payment method based on its category
  const getPaymentIcon = (category) => {
    const iconColor = "primary";
    const iconSize = "small";
    
    switch (category?.toLowerCase()) {
      case 'cash':
        return <MonetizationOnIcon color={iconColor} fontSize={iconSize} />;
      case 'card':
        return <CreditCardIcon color={iconColor} fontSize={iconSize} />;
      case 'bank_transfer':
        return <AccountBalanceIcon color={iconColor} fontSize={iconSize} />;
      case 'mobile_wallet':
        return <SmartphoneIcon color={iconColor} fontSize={iconSize} />;
      default:
        return <PaymentsIcon color={iconColor} fontSize={iconSize} />;
    }
  };

  // Format the date for display in the payment history
  const formatPaymentDate = (date) => {
    if (!date) return 'N/A';
    
    try {
      if (date instanceof Date) {
        return date.toLocaleDateString();
      } else if (typeof date === 'string') {
        return new Date(date).toLocaleDateString();
      } else {
        return 'N/A';
      }
    } catch (e) {
      return 'N/A';
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        mb: 1.5, 
        borderRadius: '4px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        backgroundColor: '#f8faff',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 1.5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CurrencyRupeeIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Refund Payment
            </Typography>
          </Box>
        </Box>
      </Box>
      
      <Collapse in={true} timeout="auto">
        <Box sx={{ p: 2 }}>
          {/* Payment History */}
          {payments.length > 0 ? (
            <Box>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 1.5
              }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Payments Received
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.75,
                  backgroundColor: '#e8f4ff',
                  borderRadius: 20,
                  border: '1px solid rgba(33, 150, 243, 0.2)'
                }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    Total Paid:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {totalPaid.toFixed(2)} SAR
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ 
                maxHeight: '240px', 
                overflow: 'auto', 
                pr: 0.5,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                },
              }}>
                <Stack spacing={1}>
                  {payments.map((payment, index) => (
                    <Box 
                      key={payment.id || index}
                      sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.5,
                        borderRadius: '4px',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        backgroundColor: 'white',
                        '&:hover': {
                          backgroundColor: '#f5f9ff'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {getPaymentIcon(payment.category || 'cash')}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {payment.method}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatPaymentDate(payment.date)} {payment.reference && `• ${payment.reference}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {parseFloat(payment.amount).toFixed(2)} SAR
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
              
              {/* Balance Info */}
              {totalPaid < grandTotal && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 2,
                    p: 1.5,
                    backgroundColor: '#fff8e1',
                    borderRadius: '4px',
                    border: '1px dashed #ffd54f'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoOutlinedIcon color="warning" fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Remaining Balance
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} color="warning.dark">
                    {remainingBalance.toFixed(2)} SAR
                  </Typography>
                </Box>
              )}
              
              {/* Change Info */}
              {totalPaid > grandTotal && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 2,
                    p: 1.5,
                    backgroundColor: '#e8f5e9',
                    borderRadius: '4px',
                    border: '1px dashed #a5d6a7'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoOutlinedIcon color="success" fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Change Amount
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} color="success.dark">
                    {(totalPaid - grandTotal).toFixed(2)} SAR
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              backgroundColor: 'white',
              borderRadius: '4px',
              border: '1px dashed rgba(0, 0, 0, 0.15)',
            }}>
              <Stack sx={{ 
                backgroundColor: '#fff',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  py: 0.75,
                  px: 1.5,
                  backgroundColor: '#fff',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Bill Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {initialTotalBillAmount.toFixed(2)} SAR
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  py: 0.75,
                  px: 1.5,
                  backgroundColor: '#f5f5f5',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Refund Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="error.main">
                    {totalRefundAmount.toFixed(2)} SAR
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default DebitNotePaymentSection;
